import ProductEntity from "@modules/products/entities/ProductEntity";
import { BehaviorSubject, Observable, combineLatest } from "rxjs";
import { map, tap } from "rxjs/operators";

import type ProductVariantsEntity from "../../entities/ProductVariantsEntity";

type selectedOptionIndex =
    | "selectedOption1$"
    | "selectedOption2$"
    | "selectedOption3$";

class SelectProductVariantManager {
    selectedProductVariant$: Observable<ProductVariantsEntity | null>;
    selectedOption1$ = new BehaviorSubject<string | null>(null);
    selectedOption2$ = new BehaviorSubject<string | null>(null);
    selectedOption3$ = new BehaviorSubject<string | null>(null);

    constructor(
        private readonly product$: Observable<ProductEntity | null>,
        private readonly defaultVariantId$: Observable<number | null>
    ) {
        combineLatest([this.product$, this.defaultVariantId$])
            .pipe(
                tap(([product, variantId]) => {
                    const variant = product?.variants?.find(
                        (variant) => variant.id === variantId
                    );
                    this.selectedOption1$.next(
                        variant?.options[0]?.value || null
                    );
                    this.selectedOption2$.next(
                        variant?.options[1]?.value || null
                    );
                    this.selectedOption3$.next(
                        variant?.options[2]?.value || null
                    );
                })
            )
            .subscribe();

        this.selectedProductVariant$ = combineLatest([
            this.selectedOption1$,
            this.selectedOption2$,
            this.selectedOption3$,
            this.product$,
        ]).pipe(
            map(([so1, so2, so3, product]) => {
                return (
                    product?.variants?.find(
                        (v) =>
                            (v?.options[0]?.value || null) === so1 &&
                            (v?.options[1]?.value || null) === so2 &&
                            (v?.options[2]?.value || null) === so3
                    ) || null
                );
            })
        );
    }

    changeOption(option: { value: string; index: number }): void {
        const position = option.index + 1;
        if (position === 1) {
            this.selectedOption1$.next(option.value);
        } else if (position === 2) {
            this.selectedOption2$.next(option.value);
        } else if (position === 3) {
            this.selectedOption3$.next(option.value);
        }
    }

    getByIndex(index: number): string | null {
        return (
            this[
                `selectedOption${index + 1}$` as selectedOptionIndex
            ].getValue() || null
        );
    }
}

export default SelectProductVariantManager;
